import React, { FC, memo, useEffect, useRef, useMemo } from 'react';
import { WarehouseForBooking } from 'entities/Warehouse';
import { GeolocationCoords } from 'app/types/common';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ModernWarehouseCard } from 'features/WarehouseCard';
import { calculateDistance } from 'widgets/SelectWarehouseMap/utils/helpers/calculateDistance';
import { useGeolocation } from 'shared/utils/hooks/useGeolocation';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getWarehouseInfo } from 'pages/BookingPage/model/selectors/getWarehouseInfo';
import styles from './ModernWarehousesList.module.scss';

interface ModernWarehousesListProps {
  warehouses: WarehouseForBooking[] | undefined;
  outlinedWarehouseId: Nullable<string>;
  onSelectCard: (lat: number, lng: number, cardId: string) => void;
  onGetRoute: (coords: GeolocationCoords, warehouseId: string) => void;
  onClearRoute: () => void;
  onSelectWarehouse: (warehouse: WarehouseForBooking) => void;
}

export const ModernWarehousesList: FC<ModernWarehousesListProps> = memo((props) => {
  const { warehouses, outlinedWarehouseId, onSelectCard, onGetRoute, onClearRoute, onSelectWarehouse } = props;

  const { t } = useAppTranslation('booking');

  const userCoords = useGeolocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Get the selected warehouse from booking state
  const selectedWarehouseFromBooking = useAppSelector(getWarehouseInfo);

  // Create refs map for warehouse cards
  const warehouseRefs = useMemo(() => {
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {};
    warehouses?.forEach((warehouse) => {
      refs[warehouse.warehouseId] = React.createRef<HTMLDivElement>();
    });
    return refs;
  }, [warehouses]);

  // Scroll to selected warehouse when component mounts or when selected warehouse changes
  useEffect(() => {
    if (!warehouses) return;

    const warehouseToScrollTo = outlinedWarehouseId || selectedWarehouseFromBooking?.warehouseId;
    if (!warehouseToScrollTo) return;

    const targetRef = warehouseRefs[warehouseToScrollTo];
    if (targetRef?.current) {
      // Find the index of the warehouse to determine if it's the last one
      const warehouseIndex = warehouses.findIndex((w) => w.warehouseId === warehouseToScrollTo);
      const isLastWarehouse = warehouseIndex === warehouses.length - 1;

      // Scroll to the warehouse card with appropriate behavior
      targetRef.current.scrollIntoView({
        behavior: 'smooth',
        block: isLastWarehouse ? 'end' : 'center', // Use 'end' for last item, 'center' for others
        inline: 'nearest',
      });
    }
  }, [warehouses, outlinedWarehouseId, selectedWarehouseFromBooking?.warehouseId, warehouseRefs]);

  return (
    <div className="absolute top-6 right-6 bottom-6 bg-white rounded-lg shadow-lg w-[560px] z-10 overflow-hidden flex flex-col">
      <div className="p-4 pb-2 flex-shrink-0">
        <h2 className="text-3xl font-semibold font-tektur text-primary">{t('Our objects:')}</h2>
      </div>
      <div ref={scrollContainerRef} className={`overflow-y-auto flex-1 px-4 pb-4 pt-2 ${styles.scrollContainer}`}>
        <div className="space-y-4">
          {warehouses?.length ? (
            warehouses.map((warehouse) => {
              // Use outlined warehouse ID or fall back to selected warehouse from booking state
              const isOutlined =
                outlinedWarehouseId === warehouse.warehouseId ||
                (!outlinedWarehouseId && selectedWarehouseFromBooking?.warehouseId === warehouse.warehouseId);

              const distance = userCoords
                ? roundNumber(calculateDistance(userCoords.lat, userCoords.lng, warehouse.latitude, warehouse.longitude))
                : null;

              const selectCard = (coords: GeolocationCoords): void => {
                onSelectCard(coords.lat, coords.lng, warehouse.warehouseId);
              };

              const setNextStep = (): void => {
                onSelectWarehouse(warehouse);
              };

              return (
                <div key={warehouse.warehouseId} ref={warehouseRefs[warehouse.warehouseId]}>
                  <ModernWarehouseCard
                    warehouse={warehouse}
                    distance={distance}
                    isOutlined={isOutlined}
                    onGetRoute={onGetRoute}
                    onClearRoute={onClearRoute}
                    onViewBoxes={setNextStep}
                    onSelectCard={selectCard}
                  />
                </div>
              );
            })
          ) : (
            <div className="text-center text-gray-500 py-8">{t('No warehouses found')}</div>
          )}
        </div>
      </div>
    </div>
  );
});
